import { money } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { BookOpen, Download, MapPin } from "lucide-react";

// Transaction ledger with region breakdown + Day/Month/Quarter/Year roll-ups.
export const LedgerTab = ({
  ledger, ledgerProvince, setLedgerProvince, rangeStart, rangeEnd, setRangeStart, setRangeEnd,
  loadLedger, exportLedger, rollup, setRollup,
}) => (
  <div className="rounded-xl border border-black/10 bg-white p-5">
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary"><BookOpen className="h-4 w-4" /></div>
          <h3 className="font-heading text-lg" style={{ fontWeight: 700 }}>Transaction ledger</h3>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">Every settled split on the platform, broken down by day and region.</p>
      </div>
      <div className="flex items-end gap-2">
        <div>
          <Label className="text-[11px]">From</Label>
          <Input data-testid="ledger-start" type="date" value={rangeStart}
            onChange={(e) => { setRangeStart(e.target.value); }} className="mt-1 h-9 w-[150px]" />
        </div>
        <div>
          <Label className="text-[11px]">To</Label>
          <Input data-testid="ledger-end" type="date" value={rangeEnd}
            onChange={(e) => { setRangeEnd(e.target.value); }} className="mt-1 h-9 w-[150px]" />
        </div>
        <div>
          <Label className="text-[11px]">Region</Label>
          <select data-testid="ledger-region" value={ledgerProvince}
            onChange={(e) => { setLedgerProvince(e.target.value); loadLedger(e.target.value); }}
            className="mt-1 flex h-9 w-[150px] rounded-md border border-input bg-background px-2 text-sm">
            <option value="">All regions</option>
            {(ledger?.regions || []).map((r) => <option key={r} value={r === "Canada-wide" ? "" : r}>{r}</option>)}
          </select>
        </div>
        <Button data-testid="ledger-apply" size="sm" className="rounded-full" onClick={() => loadLedger()}>Apply</Button>
        <Button data-testid="ledger-export" size="sm" variant="outline" className="rounded-full" onClick={exportLedger}>
          <Download className="mr-1.5 h-4 w-4" /> Download CSV
        </Button>
      </div>
    </div>

    {ledger && (
      <>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            ["Transactions", ledger.totals.count, "bg-secondary text-foreground", false],
            ["Gross volume", ledger.totals.gross, "bg-blue-100 text-blue-700", true],
            ["Sales tax collected", ledger.totals.tax, "bg-purple-100 text-purple-700", true],
            ["Paid to owners", ledger.totals.owner_payout, "bg-green-100 text-green-700", true],
            ["Platform revenue", ledger.totals.revenue, "bg-accent/15 text-accent", true],
          ].map(([label, val, cls, isMoney]) => (
            <div key={label} className="rounded-lg border border-black/5 p-3">
              <p className="text-[11px] text-muted-foreground">{label}</p>
              <p className={`mt-0.5 inline-block rounded px-1.5 font-heading text-lg ${cls}`} style={{ fontWeight: 700 }}>
                {isMoney ? money(val) : val}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div>
            <p className="text-xs font-semibold text-foreground/70">By region</p>
            <div className="mt-2 rounded-lg border border-black/10 divide-y divide-black/5" data-testid="ledger-by-region">
              {ledger.by_region.length === 0 ? <p className="p-3 text-sm text-muted-foreground">No data.</p> :
                ledger.by_region.map((r) => (
                  <div key={r.region} className="flex items-center justify-between p-3 text-sm">
                    <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-muted-foreground" /> {r.region}</span>
                    <span className="text-muted-foreground">{r.count} txns</span>
                    <span className="font-heading" style={{ fontWeight: 700 }}>{money(r.gross)}</span>
                  </div>
                ))}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-foreground/70">Roll-up</p>
              <div className="flex gap-1" data-testid="ledger-rollup-toggle">
                {[["day", "Day"], ["month", "Month"], ["quarter", "Quarter"], ["year", "Year"]].map(([k, lbl]) => (
                  <button
                    key={k}
                    data-testid={`rollup-${k}`}
                    onClick={() => setRollup(k)}
                    className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                      rollup === k ? "bg-foreground text-background" : "text-foreground/60 hover:bg-secondary"
                    }`}
                  >
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-2 rounded-lg border border-black/10 divide-y divide-black/5 max-h-[220px] overflow-y-auto" data-testid="ledger-by-day">
              {(() => {
                const rows = ledger[`by_${rollup}`] || [];
                return rows.length === 0 ? <p className="p-3 text-sm text-muted-foreground">No data.</p> :
                  rows.map((r) => (
                    <div key={r.date || r.period} className="flex items-center justify-between p-3 text-sm">
                      <span>{r.date || r.period}</span>
                      <span className="text-muted-foreground">{r.count} txns</span>
                      <span className="font-heading" style={{ fontWeight: 700 }}>{money(r.gross)}</span>
                    </div>
                  ));
              })()}
            </div>
          </div>
        </div>

        <p className="mt-6 text-xs font-semibold text-foreground/70">All transactions</p>
        <div className="mt-2 overflow-x-auto rounded-lg border border-black/10">
          <table className="w-full text-sm" data-testid="ledger-table">
            <thead className="bg-secondary/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="p-3">Date</th><th className="p-3">Region</th><th className="p-3">Deal</th>
                <th className="p-3 text-right">Gross</th><th className="p-3 text-right">Sales tax</th><th className="p-3 text-right">Owner payout</th><th className="p-3 text-right">Revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/5">
              {ledger.rows.length === 0 ? (
                <tr><td colSpan={7} className="p-4 text-center text-muted-foreground">No transactions in this range.</td></tr>
              ) : ledger.rows.map((r) => (
                <tr key={r.match_id} className="hover:bg-secondary/30">
                  <td className="p-3 whitespace-nowrap">{r.date}</td>
                  <td className="p-3"><Badge variant="outline" className="font-normal">{r.region}</Badge></td>
                  <td className="p-3 max-w-[220px] truncate">{r.deal_title}</td>
                  <td className="p-3 text-right">{money(r.gross)}</td>
                  <td className="p-3 text-right text-purple-700">{money(r.tax)}</td>
                  <td className="p-3 text-right text-green-700">{money(r.owner_payout)}</td>
                  <td className="p-3 text-right text-accent">{money(r.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    )}
  </div>
);

export default LedgerTab;
